import type { StorylineRtlPlusContentType } from 'types/storylines';
import type { VideoProps } from 'types/videoTypes';
import type { EpgEntry, EpgImageObject } from './epg';
import { formatIsoWithOffset, getDateFormat } from './time';

export const RtlPlusEpisodeVideoFactory = (
  rtlPlusEpisode: StorylineRtlPlusContentType,
  longformSourceID: string
): VideoProps => {
  return {
    id: rtlPlusEpisode.id ?? '',
    sourceId: longformSourceID ?? '',
    lastmodifiedAt: rtlPlusEpisode.lastmodifiedAt ?? '',
    createdAt: rtlPlusEpisode.createdAt ?? '',
    publishedAt: rtlPlusEpisode.publishedAt ?? '',
    type: 'vod',
    fields: {
      title: rtlPlusEpisode.fields?.title ?? '',
      description: rtlPlusEpisode.fields?.['short-text'] ?? undefined,
      'ads-free': 'false',
      producer: 'RTL',
      'regions-allowed': 'de',
    },
    image: rtlPlusEpisode.image,
    url: rtlPlusEpisode.fields?.url ?? '',
    duration: rtlPlusEpisode?.fields?.duration ? Number(rtlPlusEpisode.fields.duration) : undefined,
  };
};

export const EpgVideoFactory = (epgEntry: EpgEntry): VideoProps => {
  const getTitle = (): string => {
    if (epgEntry?.type === 'epg-catchup' && epgEntry?.model?.fields?.fields?.startDate) {
      const date = getDateFormat(
        epgEntry?.model?.fields?.fields?.firstAiredDate ?? epgEntry.model.fields.fields.startDate
      );
      return date ? `Sendung vom ${date}` : (epgEntry?.model?.fields?.fields?.name ?? '');
    }

    return epgEntry?.model?.fields?.fields?.name ?? '';
  };

  const getDescription = (): string => {
    return epgEntry?.type === 'rtlplus-episode' && epgEntry?.model?.fields?.fields?.abstract
      ? epgEntry?.model?.fields?.fields?.abstract
      : (epgEntry?.model?.fields?.fields?.description ?? '');
  };

  const durationField = epgEntry?.model?.fields?.fields?.['rtlPlus.duration'];
  let duration: number | undefined = undefined;
  if (typeof durationField === 'string') {
    const match = /^PT(\d+)S$/.exec(durationField);
    if (match) {
      duration = Number(match[1]);
    }
  }

  const getDefaultImage = (imageObject?: EpgImageObject | null): string | undefined => {
    if (!imageObject?.all) return undefined;
    return imageObject.all.find((image) => image.includes('/default/'));
  };

  const getProducer = (): string => {
    const offer = epgEntry?.model?.fields?.fields?.offers?.find((offer) => offer.seller);
    return offer?.seller?.name ?? 'RTL';
  };

  return {
    id: epgEntry?.model?.fields?.fields?.bsoKey ?? '',
    sourceId: epgEntry?.model?.fields?.fields?.bsoKey ?? '',
    lastmodifiedAt: formatIsoWithOffset(epgEntry?.model?.lastmodifiedAt),
    createdAt: formatIsoWithOffset(epgEntry?.model?.createdAt),
    publishedAt: formatIsoWithOffset(epgEntry?.model?.publishedAt),
    firstAiredDate: epgEntry?.model?.fields?.fields?.firstAiredDate ?? undefined,
    type: 'vod',
    fields: {
      title: getTitle(),
      description: getDescription(),
      'ads-free': 'false',
      producer: getProducer(),
      'regions-allowed': 'de',
      episodeNumber: epgEntry?.model?.fields?.fields?.episodeNumber ?? undefined,
      caption: epgEntry?.model?.fields?.fields?.caption ?? undefined,
    },
    image: {
      baseUrl: epgEntry?.model?.fields?.image?.baseUrl ?? getDefaultImage(epgEntry?.model?.fields?.image) ?? '',
      defaultUrl: epgEntry?.model?.fields?.image?.defaultUrl ?? getDefaultImage(epgEntry?.model?.fields?.image) ?? '',
      isExtern: true,
    },
    url: epgEntry?.model?.url ?? '',
    duration: duration,
    tags: [{ name: 'longform' }, { name: epgEntry?.model?.fields?.fields?.alternateName ?? '' }],
  };
};
